<template>
  <div class="agent-detail-container">
    <div class="header-section mb-2">
      <v-btn variant="text" prepend-icon="mdi-arrow-left" to="/agent" class="pl-0 text-none">
        {{ $t('agent.allAgents').toLowerCase() }}
      </v-btn>

      <div class="d-flex justify-space-between align-center mt-2" v-if="agent">
        <h1 class="text-h4 d-flex align-center">
          <v-icon size="large" class="mr-3" :color="getIconColor(agent.name)">{{ getAgentIcon(agent.name) }}</v-icon>
          {{ agent.name }}
        </h1>
        <v-btn v-if="conversation.length > 0" color="justgray" variant="outlined" @click="clearConversation">
          <v-icon class="mr-2">mdi-delete</v-icon>
          {{ $t('agent.clearConversation') || 'Clear Conversation' }}
        </v-btn>
      </div>
    </div>

    <v-card v-if="agent" class="mb-3">
      <v-card-text>
        <div class="mb-2">
          <strong>{{ $t('agent.description') }}:</strong> {{ agent.description || $t('agent.noDescription') }}
        </div>
        <div class="mb-2">
          <strong>{{ $t('agent.instruction') }}:</strong>
          <div>{{ agent.instruction.substring(0, 1000) + '...' }}</div>
        </div>
        <div class="mb-2">
          <strong>{{ $t('agent.model') }}:</strong> {{ agent.model }}
        </div>
        <div v-if="agent.data && agent.data.length > 0" class="mb-2">
          <strong>Data:</strong>
          <v-chip-group selected-class="text-info">
            <v-chip v-for="(dataItem, dataIndex) in agent.data" :key="dataIndex" size="small" color="info" variant="outlined" :value="dataIndex" select="true">
              {{ dataItem }}
            </v-chip>
          </v-chip-group>
        </div>
        <div class="mb-2">
          <strong>{{ $t('agent.tools') }}:</strong>
          <v-chip-group selected-class="text-primary">
            <v-chip v-for="(tool, toolIndex) in agent.tools" :key="toolIndex" size="small" color="primary" variant="outlined" :value="toolIndex" select="true">
              {{ tool }}
            </v-chip>
          </v-chip-group>
        </div>
        <div v-if="agent.linkedAgents && agent.linkedAgents.length > 0" class="mb-4">
          <strong>{{ $t('agent.linkedAgents') }}:</strong>
          <div class="d-flex flex-wrap gap-1 mt-1">
            <v-chip-group selected-class="text-secondary">
              <v-chip v-for="(linkedAgent, linkIndex) in agent.linkedAgents" :key="linkIndex" size="small" color="secondary" variant="outlined" :value="linkIndex" select="true">
                {{ linkedAgent }}
              </v-chip>
            </v-chip-group>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Main scrollable content area -->
    <div class="conversation-wrapper">
      <!-- Initial query input when no conversation exists yet -->
      <div v-if="conversation.length === 0" class="mb-6 position-relative">
        <v-textarea
          v-model="userQuery"
          :label="$t('agent.enterQuery')"
          rows="4"
          auto-grow
          hide-details
          variant="outlined"
          rounded="lg"
          class="pr-16"
          @keydown.enter.prevent="!$event.shiftKey && submitQuery()"
        ></v-textarea>

        <v-btn
          color="primary"
          icon="mdi-send"
          @click="submitQuery"
          class="submit-btn"
          :disabled="!userQuery.trim()"
          size="small"
        ></v-btn>
      </div>

      <!-- Conversation history with alternating messages -->
      <div v-if="conversation.length > 0" class="conversation-container mb-6" ref="conversationContainer">
        <div v-for="(message, index) in conversation" :key="index" class="mb-4">
          <!-- User message -->
          <div v-if="message.type === 'user'" class="user-message d-flex align-start mb-2">
            <v-avatar size="36" class="mt-1 mr-3" color="grey-lighten-2">
              <v-icon>mdi-account</v-icon>
            </v-avatar>
            <div class="user-bubble-container position-relative">
              <div class="user-bubble pa-3 rounded-lg">{{ message.content }}</div>
              <div class="copy-button-container">
                <v-btn
                  density="compact"
                  variant="text"
                  size="small"
                  @click="copyToClipboard(message.content, index)"
                  class="copy-button"
                >
                  <div class="copy-button-content">
                    <v-icon :icon="copyStates[index] ? 'mdi-check' : 'mdi-content-copy'"></v-icon>
                    <span class="copy-text" v-if="!copyStates[index]">Copy</span>
                  </div>
                </v-btn>
              </div>
            </div>
          </div>

          <!-- Agent response -->
          <div v-else class="agent-message d-flex align-start mb-2">
            <v-avatar size="36" class="mt-1 mr-3" :color="getIconColor(agent?.name || '')">
              <v-icon>{{ getAgentIcon(agent?.name || '') }}</v-icon>
            </v-avatar>
            <div class="response-container position-relative">
              <div class="response-content pa-3 rounded-lg markdown-body" v-html="renderMarkdown(message.content)"></div>
              <div class="copy-button-container" v-if="!message.loading">
                <v-btn
                  density="compact"
                  variant="text"
                  size="small"
                  @click="copyToClipboard(message.content, index)"
                  class="copy-button"
                >
                  <div class="copy-button-content">
                    <v-icon :icon="copyStates[index] ? 'mdi-check' : 'mdi-content-copy'"></v-icon>
                    <span class="copy-text" v-if="!copyStates[index]">Copy</span>
                  </div>
                </v-btn>
              </div>
            </div>
          </div>
        </div>

        <!-- New query input at the bottom of conversation -->
        <div class="position-relative mt-4">
          <v-textarea
            v-model="userQuery"
            :label="$t('agent.enterQuery')"
            rows="3"
            auto-grow
            hide-details
            variant="outlined"
            rounded="lg"
            class="pr-16"
            @keydown.enter.prevent="!$event.shiftKey && submitQuery()"
          ></v-textarea>

          <v-btn
            color="primary"
            icon="mdi-send"
            @click="submitQuery"
            class="submit-btn"
            :disabled="!userQuery.trim()"
            size="small"
          ></v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { userStore } from '@/stores/userStore';
import { createEventSource } from 'eventsource-client';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { v4 as uuid } from 'uuid';
import { computed, ref, onMounted, nextTick, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
// Define Agent interface
interface Agent {
  id: string;
  name: string;
  instruction: string;
  description: string;
  model: string;
  data: string[];
  tools: string[];
  linkedAgents: string[];
}

const route = useRoute();
const agentId = computed(() => route.params.id as string);

const userQuery = ref('');
const threadId = ref<string | null>(null);

// Define message interface
interface Message {
  type: 'user' | 'agent';
  content: string;
  loading?: boolean;
  error?: boolean;
}

// Store conversation history
const conversation = ref<Message[]>([]);
const agent = ref<Agent | null>(null);
const conversationContainer = ref<HTMLElement | null>(null);
const copyStates = ref<Record<number, boolean>>({});

// Function to clear the conversation and reset the threadId
const clearConversation = () => {
  conversation.value = [];
  threadId.value = null;
};

// Function to get appropriate icon based on agent name
const getAgentIcon = (agentName: string) => {
  const name = agentName.toLowerCase();

  if (name.includes('nextgen')) return 'mdi-update';
  if (name.includes('lean')) return 'mdi-chart-timeline-variant';
  if (name.includes('api')) return 'mdi-api';
  if (name.includes('iobeya')) return 'mdi-application';
  if (name.includes('coordinator')) return 'mdi-account-supervisor';
  if (name.includes('sales')) return 'mdi-currency-usd';
  if (name.includes('discovery')) return 'mdi-file-document-outline';

  // Default icon
  return 'mdi-robot';
};

// Function to get color for icon based on agent name
const getIconColor = (agentName: string) => {
  const name = agentName.toLowerCase();

  if (name.includes('nextgen')) return 'info';
  if (name.includes('lean')) return 'success';
  if (name.includes('api')) return 'warning';
  if (name.includes('iobeya')) return 'primary';
  if (name.includes('coordinator')) return 'secondary';
  if (name.includes('sales')) return 'amber-darken-2';
  if (name.includes('discovery')) return 'indigo';

  // Default color
  return 'primary';
};

// Function to render markdown content safely with marked library
const renderMarkdown = (content: string): string => {
  try {
    // Configure marked with proper options for v15.0.3
    // Create a custom renderer to ensure security and styling
    const renderer = new marked.Renderer();

    // Set marked options using only supported properties
    marked.use({
      renderer: renderer,
      gfm: true,           // GitHub Flavored Markdown
      breaks: true,        // Convert line breaks to <br>
      pedantic: false,     // Don't be pedantic about markdown spec
    });

    // Convert markdown to HTML and handle potential Promise return type
    const rawHtml = marked.parse(content);

    // Check if the result is a string (not a Promise)
    if (typeof rawHtml === 'string') {
      // Sanitize HTML to prevent XSS attacks with specific allowed elements and attributes
      return DOMPurify.sanitize(rawHtml, {
        USE_PROFILES: { html: true },
        ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'hr', 'ul', 'ol', 'li',
                      'strong', 'em', 'a', 'blockquote', 'code', 'pre', 'table', 'thead', 'tbody',
                      'tr', 'th', 'td', 'img', 'span', 'div'],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'class', 'title']
      });
    } else {
      // Handle the case where marked.parse returns a Promise
      console.warn('Markdown parsing returned a Promise, using sanitized content');
      return DOMPurify.sanitize(content); // Return sanitized original content if parsing fails
    }
  } catch (error) {
    console.error('Error rendering markdown:', error);
    return DOMPurify.sanitize(content); // Return sanitized original content if parsing fails
  }
};

// Sample agent data - in a real app, this would come from an API or store

const agents: Agent[] = [
  {
    id: '1',
    name: 'NextGen Expert',
    instruction: `You are an AI assistant specialized in answering questions about the Iobeya software new Generation (called NextGen). Your task is to provide accurate and helpful responses to user queries using the information retrieved from the provided context.

To answer the user's question, follow these steps:

1. Carefully read and analyze the provided context.
2. Identify the most relevant information that directly addresses the user's question.
3. If the context contains multiple relevant pieces of information, synthesize them to provide a comprehensive answer.
4. Ensure that your response is clear, concise, and directly answers the user's question.
5. If the context contains technical terms or concepts, briefly explain them in simple language.
6. If there are any limitations or conditions mentioned in the documentation, include them in your response.

When formulating your response:
- Begin with a direct answer to the question, if possible.
- Provide additional context or explanations as needed.
- Use bullet points or numbered lists for clarity when appropriate.
- If relevant, mention any related features or functionalities that might be useful to the user.

If the provided context does not contain sufficient information to answer the user's question:
- State that you don't have enough information to provide a complete answer.
- Offer any partial information that might be relevant or helpful.
- Suggest that the user contact Iobeya support for more detailed information.

Format your response as follows:
1. Start with <answer> tags.
2. Provide your response to the user's question.
3. If you're quoting directly from the provided context, use quotation marks and mention it's from the provided context.
4. End with </answer> tags.`,
    description: 'Expert agent specialized in iObeya NextGen platform features and capabilities',
    model: 'Claude 3.5 Sonnet',
    data: ['iObeya Glossary'],
    tools: ['Think'],
    linkedAgents: []
  },
  {
    id: '2',
    name: 'Lean Expert',
    instruction: `You are an AI assistant specializing in lean management. Your role is to provide accurate and helpful information about lean management concepts, principles, and practices to users.

When responding to a user query, follow these steps:

1. Analyze the query:
   - Identify the main topic or concept related to lean management
   - Determine if the query is within the scope of lean management
   - List key concepts from the lean management info that are relevant to the query
   - Identify any specific tools or techniques mentioned in the query
   - Consider how the query relates to the core principles of lean management

2. Based on your analysis, take one of the following actions:

   a. If the request contains website or URL:
      - Trigger the appropriate agent by writing: <action>Call Web Search Preview</action> or <action>None</action>
      - Provide the user's request for the triggered agent to address.
      - Extract key facts and data points from web content
      - Provide direct quotes and citations when appropriate
      - Summarize findings in a clear, concise manner
      - Include source URLs when reporting information

   b. If the request does not contain website or URL, formulate your response:
      - For lean management queries, use the provided information to craft a clear, concise answer
      - For related topics, explain the connection to lean management and provide relevant information
      - For unrelated queries, politely inform the user that the question is outside your area of expertise`,
    description: 'Expert in lean management principles, methodologies, and best practices',
    model: 'GPT 4o',
    data: [],
    tools: ['Web Search', 'Think'],
    linkedAgents: []
  },
  {
    id: '3',
    name: 'NextGen API Assistant',
    instruction: `You are an AI agent designed to interact with iObeya APIs. Your primary function is to interpret business requests from users and decompose them into a series of appropriate API calls. Here's how you should approach this task:

1. First, review the available API calls you can use.

2. Review the API object model.

3. Carefully analyze the user's business request.

4. Break down the user's request into discrete steps that can be accomplished using the available API calls. Consider the logical order of operations and any dependencies between steps.

5. For each step, identify the most appropriate API call from the available options. Make sure to match the functionality of the API with the requirements of each step.

6. Structure your response as follows:

<response>
<analysis>
[Provide a brief analysis of the user's request and your approach to fulfilling it]
</analysis>

<api_calls>
[List each API call you plan to make, in order, with a brief explanation of its purpose]
1. [API call 1]: [Purpose]
2. [API call 2]: [Purpose]
...</api_calls>

<explanation>
[Explain how this sequence of API calls will fulfill the user's request]
</explanation>
</response>`,
    description: 'API specialist that translates business requirements into iObeya API operations',
    model: 'Claude 3.5 Sonnet',
    data: ['API model'],
    tools: ['Think', 'iObeya API'],
    linkedAgents: []
  },
  {
    id: '4',
    name: 'iObeya Expert',
    instruction: `You are an AI assistant specialized in answering questions about the Iobeya software. Your task is to provide accurate and helpful responses to user queries using the information retrieved from the provided context.

To answer the user's question, follow these steps:

1. Call the search Iobeya Documentation Tool to retrieve relevant information about the user's request. Use the results to populate your context.
2. Carefully read and analyze the provided context.
3. Identify the most relevant information that directly addresses the user's question.
4. If the context contains multiple relevant pieces of information, synthesize them to provide a comprehensive answer.
5. Ensure that your response is clear, concise, and directly answers the user's question.
6. If the context contains technical terms or concepts, briefly explain them in simple language.
7. If there are any limitations or conditions mentioned in the documentation, include them in your response.

When formulating your response:
- Begin with a direct answer to the question, if possible.
- Provide additional context or explanations as needed.
- Use bullet points or numbered lists for clarity when appropriate.
- If relevant, mention any related features or functionalities that might be useful to the user.`,
    description: 'Expert in iObeya platform documentation, features and capabilities',
    model: 'Claude 3.5 Sonnet',
    data: ['iObeya Glossary', 'Resource Center'],
    tools: ['Resource Center Search', 'Think'],
    linkedAgents: []
  },
  {
    id: '5',
    name: 'Coordinator',
    instruction: `You are a coordinator agent responsible for analyzing user requests and routing them to specialized agents based on the content. Your primary function is to determine whether a request is related to lean methodology or iObeya software, and direct it to the appropriate specialized agent.

Before processing any request, ensure you have the user's information (firstname, lastname, language, email, company, and workspaceId). If any of this information is missing from your memory, you must ask the user for it.

You have access to the following specialized agents:
1. Lean Agent: Expert on lean methodology
2. iObeya Agent: Specialized in the iObeya software
3. iObeya API Agent: Specialized in the iObeya API
4. Think Agent: An agent that can help with the processing of think
5. NextGen Agent: Specialized in the iObeya software (the NextGen version)

Process:
1. Check user information and request any missing details.
2. Analyze the user's request using the <request_evaluation> tags provided below.
3. Based on your analysis, either trigger a specialized agent or inform the user you cannot assist.
4. If triggering an agent, provide a concise summary of the user's request for the agent to address.
5. Return the specialized agent's response without additional formatting.`,
    description: 'Orchestrator that routes user requests to the appropriate specialized agents',
    model: 'Claude 3.5 Sonnet',
    data: [],
    tools: ['Think'],
    linkedAgents: ['NextGen Expert', 'Lean Expert', 'NextGen API Assistant', 'iObeya Expert']
  },
  {
    id: '6',
    name: 'Sales Assistant',
    instruction: `You are a sales assistant agent responsible for analyzing user requests. Your primary function is to retrieve customer's information.

You have three available tools:
1. Salesforce: Retrieve customer's information from Salesforce
2. Web Search: Search the web for customer's information
3. Think: Think about the user's request

`,
    description: 'Sales specialist that provides support for Salesforce and sales-related queries',
    model: 'GPT 4o',
    data: [],
    tools: ['Salesforce', 'Web Search', 'Think'],
    linkedAgents: []
  },
  {
    id: '7',
    name: 'Discovery Agent',
    instruction: `Tu es un commercial expérimenté spécialisé. À partir des notes de discovery call (transcription, enregistrement ou notes manuscrites), génères un compte-rendu structuré et actionnable pour le CRM.
Le terme "iObeya" est parfois mal retranscris dans les comptes rendus. Met le au propre.
Format de sortie attendu :
...
`,
    description: 'Generates structured CRM reports from discovery call notes and transcriptions',
    model: 'GPT 4 Turbo',
    data: [],
    tools: ['Think'],
    linkedAgents: []
  },
  {
    id: '8',
    name: 'Briefing Assistant',
    instruction: `You are an AI assistant specializing in managing HubSpot contacts, performing web searches, and creating comprehensive lead briefings. Your primary goal is to provide accurate, non-redundant, and valuable information to sales and marketing professionals.
Your tasks fall into three main categories: HubSpot Contact Management, Lead Briefings, and Web Search. Each task has specific instructions and requirements.`,
    description: 'Briefing assistant agent responsible for analyzing incoming leads or user requests. Then create a briefing based on the lead information.',
    model: 'GPT 4o',
    data: [],
    tools: ['hubspot contact', 'web search', 'Think'],
    linkedAgents: []
  }
];

// Function to scroll to the bottom of the conversation
const scrollToBottom = async () => {
  if (conversationContainer.value) {
    await nextTick();
    const container = conversationContainer.value;
    container.scrollTop = container.scrollHeight;
  }
};

// Function to load agent data and reset conversation state
const loadAgent = () => {
  // Find the agent by ID
  agent.value = agents.find(a => a.id === agentId.value) || null;

  // Reset threadId when loading a new agent
  threadId.value = null;

  // Clear the conversation when switching agents
  conversation.value = [];
};

onMounted(() => {
  loadAgent();
});

// Watch for changes to the agent ID (when navigating between agents)
watch(agentId, () => {
  loadAgent();
});

// Watch for changes to the conversation and scroll to bottom
watch(() => conversation.value, () => {
  scrollToBottom();
}, { deep: true });

const submitQuery = async () => {
  if (!userQuery.value.trim() || !agent.value) return;

  // Store the query for later reference
  const query = userQuery.value;

  // Add user message to conversation
  conversation.value.push({
    type: 'user',
    content: query
  });

  // Clear the query input after submission
  userQuery.value = '';

  // Add a message for the agent's response
  const responseIndex = conversation.value.length;
  conversation.value.push({
    type: 'agent',
    content: '',
    loading: true
  });

  try {
    const userId = userStore().id;
    if (!threadId.value) {
      threadId.value = uuid();
    }

    let streamContent = '';

    // Create the event source with POST method
    const eventSource = createEventSource({
      url: `/api/v2/agents/${agent.value.id}/stream`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: query,
        userId: userId,
        threadId: threadId.value
      }),
      onMessage: ({ data: eventData, event }) => {
        // Handle specific event types including errors
        if (event === 'error') {
          console.error('EventSource error event received');
          conversation.value[responseIndex].error = true;
          conversation.value[responseIndex].content = 'Connection error. Please try again.';
          conversation.value[responseIndex].loading = false;
          eventSource.close();
          return;
        }

        try {
          // Parse the event data
          const data = JSON.parse(eventData);

          if (data.done) {
            // Stream is complete
            conversation.value[responseIndex].loading = false;
            eventSource.close();
          } else if (data.error) {
            // Error in stream
            conversation.value[responseIndex].error = true;
            conversation.value[responseIndex].content = 'Error: ' + data.error;
            eventSource.close();
          } else if (data.text !== undefined) { // Check if text exists, even if empty
            // Append text to the response
            streamContent += data.text;
            conversation.value[responseIndex].content = streamContent;
            requestAnimationFrame(scrollToBottom);
          }
        } catch (e) {
          console.error('Error parsing SSE event:', e);
          conversation.value[responseIndex].error = true;
          conversation.value[responseIndex].content = 'Error parsing response. Please try again.';
          conversation.value[responseIndex].loading = false;
          eventSource.close();
        }
      }
    });

    // Clean up event source when component is unmounted
    onUnmounted(() => {
      if (eventSource) {
        eventSource.close();
      }
    });
  } catch (error) {
    console.error('Error calling streaming agent API:', error);

    // Update the message with an error
    conversation.value[responseIndex] = {
      type: 'agent',
      content: 'Sorry, there was an error processing your request. Please try again.',
      error: true,
      loading: false
    };
  }
}

// Function to copy message content to clipboard
const copyToClipboard = (content: string, index?: number) => {
  // Strip XML-like tags and their content from the text
  const cleanContent = content.replace(/<[^>]+>[\s\S]*?<\/[^>]+>/g, '');

  navigator.clipboard.writeText(cleanContent)
    .then(() => {
      if (index !== undefined) {
        copyStates.value[index] = true;

        // Reset the copy state after 2 seconds
        setTimeout(() => {
          copyStates.value[index] = false;
        }, 2000);
      }
    })
    .catch(err => {
      console.error('Failed to copy text: ', err);
    });
};
</script>

<style scoped>
.agent-detail-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px); /* Adjust based on your app's layout */
  overflow: hidden;
}

.header-section {
  flex: 0 0 auto;
}

.conversation-wrapper {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.conversation-container {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  max-height: calc(100vh - 250px); /* Adjust based on your header height */
  padding-right: 8px; /* Space for scrollbar */
}

.response-container {
  position: relative;
  max-width: calc(100% - 50px);
}

.response-content {
  background-color: rgba(var(--v-theme-primary), 0.1);
  border: 1px solid rgba(var(--v-theme-primary), 0.3);
  display: inline-block;
  width: 100%;
  word-break: break-word;
  color: rgba(var(--v-theme-on-surface), 0.87);
  padding: 0.5rem 0.75rem;
}

/* Add styles for markdown content with ultra-minimal spacing */
.markdown-body {
  line-height: 0.9;
  font-size: 0.95em;
}

.markdown-body :deep(*) {
  margin: 0;
  padding: 0;
}

.markdown-body :deep(p) {
  margin: 0;
  padding: 0;
  line-height: 0.9;
}

.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3),
.markdown-body :deep(h4),
.markdown-body :deep(h5),
.markdown-body :deep(h6) {
  margin-top: 0.1rem;
  margin-bottom: 0;
  font-weight: 600;
  line-height: 1;
}

.markdown-body :deep(pre) {
  background-color: rgba(0, 0, 0, 0.03);
  padding: 0.1rem;
  border-radius: 2px;
  overflow-x: auto;
  margin: 0.05rem 0;
  line-height: 0.9;
}

.markdown-body :deep(code) {
  background-color: rgba(0, 0, 0, 0.03);
  padding: 0 0.1rem;
  border-radius: 2px;
  font-family: monospace;
  font-size: 0.85em;
}

.markdown-body :deep(pre code) {
  background-color: transparent;
  padding: 0;
}

.markdown-body :deep(ul), .markdown-body :deep(ol) {
  padding-left: 0.8rem;
  margin: 0;
}

.markdown-body :deep(li) {
  margin: 0;
  padding: 0;
  line-height: 0.9;
}

.markdown-body :deep(blockquote) {
  border-left: 2px solid rgba(var(--v-theme-primary), 0.7);
  padding-left: 0.2rem;
  margin: 0.05rem 0;
  font-style: italic;
  color: rgba(var(--v-theme-on-surface), 0.7);
  line-height: 0.9;
}

.markdown-body :deep(table) {
  margin: 0.05rem 0;
  border-spacing: 0;
  border-collapse: collapse;
}

.markdown-body :deep(th), .markdown-body :deep(td) {
  padding: 0.1rem 0.2rem;
}

.markdown-body :deep(hr) {
  margin: 0.05rem 0;
  height: 1px;
}

.markdown-body :deep(a) {
  color: rgba(var(--v-theme-primary), 1);
  text-decoration: none;
}

.markdown-body :deep(a:hover) {
  text-decoration: underline;
}

.markdown-body :deep(table) {
  border-collapse: collapse;
  margin-bottom: 0.5rem;
  margin-top: 0.25rem;
  width: 100%;
}

.markdown-body :deep(th), .markdown-body :deep(td) {
  border: 1px solid rgba(0, 0, 0, 0.1);
  padding: 0.5rem;
  text-align: left;
}

.markdown-body :deep(th) {
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.position-relative {
  position: relative;
}

.submit-btn {
  position: absolute;
  bottom: 8px;
  right: 8px;
  z-index: 1;
  min-width: 32px;
  min-height: 32px;
}

.user-message, .agent-message {
  max-width: 100%;
}

.user-bubble-container {
  position: relative;
  max-width: 80%;
}

.user-bubble {
  background-color: #f0f0f0;
  word-break: break-word;
  width: 100%;
}

.copy-button-container {
  position: absolute;
  top: 0;
  right: 4px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  display: flex;
  align-items: center;
  margin: 4px;
}

.copy-text {
  font-size: 12px;
  color: white;
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 4px;
  opacity: 0;
  visibility: hidden;
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 12px;
  padding: 2px 8px;
  transition: opacity 0.2s, visibility 0.2s;
  white-space: nowrap;
}

.copy-button:hover .copy-text {
  opacity: 1;
  visibility: visible;
}

.user-bubble-container:hover .copy-button-container,
.response-container:hover .copy-button-container {
  opacity: 1;
  visibility: visible;
}

.copy-button {
  color: rgba(0, 0, 0, 0.7) !important;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  padding: 4px;
  min-width: unset !important;
}

.copy-button-content {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
}
</style>
